import React, { useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { color } from 'styled-system';
import { useSelector } from 'react-redux';
import Loader from 'theme/icons/loader';
import Text from 'theme/text';
import Close from 'theme/icons/close';
import AlertRhombus from 'theme/icons/alert-rhombus';
import OfflinePinAlt from 'theme/icons/offline-pin-alt';
import translations from 'translations';

const Wrapper = styled.div`
  position: fixed;
  bottom: 32px;
  z-index: 999;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 16px 16px;
  box-shadow: 0px -2px 60px rgba(163, 163, 164, 0.1);
  border-radius: 8px;

  ${color}
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const WrapLoader = styled.div`
  ${color}

  width: 20px;
  height: 20px;
  animation: ${rotate} 2s linear infinite;
`;

const Box = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: #fff;

  & > button {
    color: #fff;
    margin-left: 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
  }
`;

const CustomLink = styled.a`
  color: #fff;
  text-decoration: underline;

  &:hover {
    color: #fff;
    text-decoration: underline;
  }

  &:focus {
    color: #fff;
    text-decoration: underline;
  }
`;

function ReportToast({ 
  isVisible, 
  status, 
  reportPeriod, 
  downloadUrl, 
  onClose, 
  onDownload 
}) {
  const { languageCode } = useSelector((state) => state.config);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleDownload = useCallback(() => {
    if (downloadUrl && onDownload) {
      onDownload(downloadUrl);
    }
  }, [downloadUrl, onDownload]);

  if (!isVisible) return null;

  const getBackgroundColor = () => {
    switch (status) {
      case 'processing':
      case 'started':
        return 'warning';
      case 'ready':
        return 'primary_400';
      case 'error':
      case 'timeout':
        return 'red_400';
      default:
        return 'warning';
    }
  };

  const getMessage = () => {
    switch (status) {
      case 'processing':
      case 'started':
        return `${translations[languageCode].report_generation_in_progress} ${reportPeriod}`;
      case 'ready':
        return translations[languageCode].report_ready_for_download;
      case 'error':
        return translations[languageCode].report_generation_error;
      case 'timeout':
        return translations[languageCode].report_generation_timeout;
      default:
        return translations[languageCode].report_generation_in_progress;
    }
  };

  return (
    <Wrapper>
      <Content bg={getBackgroundColor()}>
        {(status === 'processing' || status === 'started') && (
          <WrapLoader color="grey_100">
            <Loader width="20" />
          </WrapLoader>
        )}

        {(status === 'processing' || status === 'started') && (
          <Text fontSize="pLarge" lineHeight="pLarge" fontWeight="medium" color="grey_100" ml="xs">
            {getMessage()}
          </Text>
        )}

                 {(status === 'error' || status === 'timeout') && (
           <Box>
             <AlertRhombus />
             <Text
               fontSize="pLarge"
               lineHeight="pLarge"
               fontWeight="medium"
               color="grey_100"
               ml="xs"
             >
               {getMessage()}
             </Text>
             <button type="button" className="close" onClick={handleClose} aria-label="Close">
               <Close />
             </button>
           </Box>
         )}

        {status === 'ready' && (
          <Box>
            <OfflinePinAlt />
            <Text
              fontSize="pLarge"
              lineHeight="pLarge"
              fontWeight="medium"
              color="grey_100"
              mr="xs"
              ml="xs"
            >
              {getMessage()}
            </Text>

            <CustomLink 
              target="_blank" 
              rel="noreferrer" 
              style={{ p: 0, m: 0 }} 
              href={downloadUrl}
              onClick={handleDownload}
            >
              {translations[languageCode].download_report_file}
            </CustomLink>

                         <button type="button" className="close" onClick={handleClose} aria-label="Close">
               <Close />
             </button>
          </Box>
        )}
      </Content>
    </Wrapper>
  );
}

export default ReportToast; 